'use client';

import type { FC } from 'react';
import type { ToolCallMessagePartComponent } from '@assistant-ui/react';
import { ProgressBar } from 'react-bootstrap';
import { Loader2Icon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toPersianDigit } from '@/helpers/utils';
import AiLikenessGauge, {
  getScoreGaugeVerdict,
  SEO_SCORE_GAUGE_ZONES
} from '@/app/ContentTools/components/ai-content-analysis/AiLikenessGauge';

export type PresentSkillOverviewArgs = {
  overview?: {
    skill?: string;
    url?: string | null;
    title?: string | null;
    meta_description?: string | null;
    score?: {
      chart_type?: 'gauge_chart';
      title?: string;
      value?: number;
    };
    metrics?: {
      chart_type?: 'card_chart';
      items?: Array<{ label?: string; value?: string }>;
    };
    dimensions?: {
      chart_type?: 'list_chart';
      title?: string;
      items?: Array<{ label?: string; value?: number; status?: string }>;
    };
  };
} & Record<string, unknown>;

const overviewBoxClass =
  'border-border bg-[var(--muted)]/20 w-full rounded-2xl border px-6 py-4';

function normalizeStatus(status?: string) {
  return (status ?? '').trim().toLowerCase();
}

function progressVariant(value: number, status?: string) {
  const key = normalizeStatus(status);
  if (
    key.includes('excellent') ||
    key.includes('good') ||
    key.includes('عالی') ||
    key.includes('خوب')
  ) {
    return 'success' as const;
  }
  if (
    key.includes('fair') ||
    key.includes('warning') ||
    key.includes('متوسط')
  ) {
    return 'warning' as const;
  }
  if (
    key.includes('weak') ||
    key.includes('poor') ||
    key.includes('fail') ||
    key.includes('ضعیف')
  ) {
    return 'danger' as const;
  }
  if (value >= 85) return 'success' as const;
  if (value >= 70) return 'info' as const;
  if (value >= 40) return 'warning' as const;
  return 'danger' as const;
}

function statusPillClass(status?: string, value?: number) {
  const variant = progressVariant(value ?? 0, status);
  if (variant === 'success') {
    return 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400';
  }
  if (variant === 'info') {
    return 'bg-sky-500/15 text-sky-600 dark:text-sky-400';
  }
  if (variant === 'warning') {
    return 'bg-amber-500/15 text-amber-700 dark:text-amber-400';
  }
  return 'bg-rose-500/15 text-rose-600 dark:text-rose-400';
}

const OverviewLoading: FC = () => (
  <div
    className={cn(
      overviewBoxClass,
      'text-muted-foreground my-2 flex items-center gap-2 text-sm'
    )}
    dir="rtl"
  >
    <Loader2Icon className="size-4 shrink-0 animate-spin" />
    در حال آماده‌سازی نمودار مهارت…
  </div>
);

export const SkillOverviewToolUI: ToolCallMessagePartComponent<
  PresentSkillOverviewArgs,
  unknown
> = function SkillOverviewToolUI({ args, status, isError }) {
  const overview = args?.overview;
  const score = overview?.score;
  const metrics = overview?.metrics?.items?.filter(
    item => item && typeof item.label === 'string' && item.label.trim()
  );
  const dimensions = overview?.dimensions?.items?.filter(
    item =>
      item &&
      typeof item.label === 'string' &&
      item.label.trim() &&
      Number.isFinite(Number(item.value))
  );

  const hasContent =
    (typeof score?.value === 'number' && Number.isFinite(score.value)) ||
    Boolean(metrics?.length) ||
    Boolean(dimensions?.length);

  if (status.type === 'running' && !hasContent) {
    return <OverviewLoading />;
  }

  if (isError) {
    return (
      <div
        className="border-destructive/40 bg-destructive/10 text-destructive my-2 rounded-2xl border px-4 py-3 text-sm"
        dir="rtl"
      >
        نمایش خلاصه مهارت ناموفق بود.
      </div>
    );
  }

  if (!hasContent) return null;

  const gaugeValue =
    typeof score?.value === 'number' && Number.isFinite(score.value)
      ? Math.max(0, Math.min(100, Math.round(score.value)))
      : null;
  const gaugeVerdict =
    gaugeValue != null ? getScoreGaugeVerdict(gaugeValue) : null;

  return (
    <div className="mcp-skill-overview mb-6 mt-2 flex w-full flex-col gap-3 px-2 sm:px-3" dir="rtl">
      {overview?.title || overview?.url ? (
        <div className="px-2">
          {overview?.title ? (
            <div className="text-foreground text-sm font-semibold">
              {overview.title}
            </div>
          ) : null}
          {overview?.url ? (
            <div
              className="text-muted-foreground mt-1 truncate text-[0.72rem]"
              dir="ltr"
            >
              {overview.url}
            </div>
          ) : null}
        </div>
      ) : null}

      {metrics?.length ? (
        <div
          className="grid w-full gap-2"
          style={{
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 7.5rem), 1fr))',
            alignItems: 'start'
          }}
        >
          {metrics.map((item, index) => (
            <div
              key={`${item.label}-${index}`}
              className={cn(overviewBoxClass, 'min-w-0 max-w-full py-3')}
            >
              <div className="text-muted-foreground mb-1 text-[0.72rem] leading-snug">
                {item.label}
              </div>
              <div className="text-foreground text-sm font-semibold leading-none">
                {toPersianDigit(String(item.value ?? ''))}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {gaugeValue != null || dimensions?.length ? (
        <div
          className="grid w-full gap-3"
          style={{
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 15rem), 1fr))',
            alignItems: 'stretch'
          }}
        >
          {gaugeValue != null ? (
            <div
              className={cn(
                overviewBoxClass,
                'flex min-w-0 max-w-full flex-col overflow-hidden'
              )}
            >
              <div className="mb-3 text-sm font-semibold">
                {score?.title || 'امتیاز'}
              </div>
              <div className="mcp-skill-overview-gauge w-full min-w-0 max-w-full overflow-hidden">
                <AiLikenessGauge
                  value={gaugeValue}
                  displayValue={toPersianDigit(String(gaugeValue))}
                  zones={SEO_SCORE_GAUGE_ZONES}
                />
              </div>
              {gaugeVerdict ? (
                <div className="mt-1 text-center">
                  <div className="text-foreground text-sm font-semibold">
                    {gaugeVerdict}
                  </div>
                  <span className="bg-primary/10 text-primary mt-2 inline-flex items-center rounded-full px-2 py-0.5 text-[0.68rem] font-medium">
                    {toPersianDigit(`${gaugeValue}`)} / ۱۰۰
                  </span>
                </div>
              ) : null}
            </div>
          ) : null}

          {dimensions?.length ? (
            <div
              className={cn(overviewBoxClass, 'min-w-0 max-w-full overflow-hidden')}
            >
              <div className="mb-3 text-sm font-semibold">
                {overview?.dimensions?.title || 'معیارها'}
              </div>
              <div className="flex w-full min-w-0 flex-col gap-3">
                {dimensions.map((item, index) => {
                  const value = Math.max(
                    0,
                    Math.min(100, Math.round(Number(item.value)))
                  );
                  const statusLabel = item.status?.trim();
                  return (
                    <div key={`${item.label}-${index}`} className="min-w-0 w-full">
                      <div className="mb-2 flex min-w-0 items-center justify-between gap-3">
                        <span className="text-foreground min-w-0 truncate text-xs">
                          {item.label}
                        </span>
                        <span className="inline-flex shrink-0 items-center gap-1.5">
                          {statusLabel ? (
                            <span
                              className={cn(
                                'inline-flex max-w-[6.5rem] items-center truncate rounded-full px-2 py-0.5 text-[0.68rem] font-medium',
                                statusPillClass(statusLabel, value)
                              )}
                            >
                              {statusLabel}
                            </span>
                          ) : null}
                          <span className="text-foreground text-xs font-semibold tabular-nums">
                            {toPersianDigit(`${value}`)}
                          </span>
                        </span>
                      </div>
                      <div className="w-full min-w-0">
                        <ProgressBar
                          now={value}
                          variant={progressVariant(value, statusLabel)}
                          className="w-100 mb-0"
                          style={{ height: 6 }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
